import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic } from 'lucide-react';
import { zineColors, zineFonts } from '../../zine/zine-theme';
import type { ConversationState } from '../contents/conversational-exercise-types';

/**
 * "On Air" live-call screen for the free practice with MellinAI: a podcast
 * studio with two guests (MellinAI the host, the student the guest), a blinking
 * ON AIR pill, live waveforms driven by the real audio levels, one big caption
 * line with MellinAI's latest sentence and a hold-to-talk button.
 *
 * Pure presentation — the connection lives in `useOpenAIRealtime` / `PracticeCall`.
 */
export interface OnAirCallViewProps {
  /** MellinAI's latest sentence (the live caption). */
  hostLine: string;
  /** Streamed words of the sentence MellinAI is saying right now ('' between responses). */
  aiLiveTranscript?: string;
  isRecording: boolean;
  isAiSpeaking: boolean;
  isAiThinking: boolean;
  currentTranscript: string;
  timeRemaining: number;
  idleSecondsLeft: number | null;
  quietSeconds: number;
  isWrappingUp: boolean;
  conversationState: ConversationState;
  error?: string | null;
  /** 0..1 loudness of both voices, read every animation frame (never through React state). */
  getLevels: () => { mic: number; ai: number };
  /** Photo for the host's circle; a red "M" circle when missing. */
  hostImageSrc?: string;
  /** Two letters for the student's circle. */
  guestInitials?: string;
  guestName?: string;
  /** Tiny grey eyebrow under the ON AIR row (the topic). */
  topicTitle?: string;
  /** Extra control for the top-right corner (audio device picker). */
  settingsSlot?: ReactNode;
  onEndConversation: () => void;
  onToggleRecording: () => void;
  onPointerDown: () => void;
  onPointerUp: () => void;
}

/** After this many quiet seconds (nobody talking, AI idle) the caption nudges the student. */
const QUIET_HINT_SEC = 10;
/** Words per second the caption reveals while her voice plays: the transcript streams in
 *  well ahead of the audio, so pacing it keeps the words roughly in step with what is heard. */
const CAPTION_WORDS_PER_SEC = 2.6;
/** Visible lines of the caption window; longer sentences roll upwards, teleprompter-style. */
const CAPTION_LINES = 3;
const BAR_COUNT = 7;
/** Relative height of each bar: tallest in the middle, like a radio VU. */
const BAR_SHAPE = [0.45, 0.7, 0.9, 1, 0.9, 0.7, 0.45];
const BAR_FLAT = 0.16;

const RED = zineColors.red;
const INK = zineColors.ink;
const GREY = '#B9ACA8';
const GREY_SOFT = '#E6DDD9';

const STYLES = `
.onair { position: relative; display: flex; flex-direction: column; box-sizing: border-box; height: var(--onair-h, auto); min-height: 440px; padding: 12px 16px 16px; background: ${zineColors.paper}; color: ${INK}; }
.onair-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.onair-pill { display: inline-flex; align-items: center; gap: 8px; padding: 7px 14px 7px 16px; border-radius: 999px; background: ${RED}; color: #fff; font-family: ${zineFonts.cond}; font-weight: 600; font-size: 14px; letter-spacing: .08em; text-transform: uppercase; }
.onair-pill-dot { width: 9px; height: 9px; border-radius: 50%; background: #fff; box-shadow: 0 0 0 2px rgba(255,255,255,.35); animation: onairBlink 1.2s ease-in-out infinite; }
.onair-meta { display: flex; align-items: center; gap: 14px; }
.onair-timer { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 22px; letter-spacing: .06em; font-variant-numeric: tabular-nums; color: ${INK}; transition: color .3s; }
.onair-timer[data-last-minute="true"] { color: ${RED}; }
.onair-rec { display: inline-flex; align-items: center; gap: 6px; font-family: ${zineFonts.cond}; font-size: 13px; letter-spacing: .12em; color: ${zineColors.warmGray}; }
.onair-rec::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: ${zineColors.warmGray}; }
.onair-rec[data-recording="true"] { color: ${RED}; }
.onair-rec[data-recording="true"]::before { background: ${RED}; animation: onairBlink 1s ease-in-out infinite; }
.onair-eyebrow { margin: 8px 0 0; text-align: center; font-family: ${zineFonts.cond}; font-size: 12px; letter-spacing: .14em; text-transform: uppercase; color: ${zineColors.warmGray}; }
.onair-error { margin: 12px auto 0; max-width: 560px; padding: 8px 12px; border-radius: 6px; background: #FDF0ED; color: ${RED}; font-size: 13px; text-align: center; }
.onair-guests { display: flex; justify-content: center; align-items: flex-start; gap: clamp(36px, 10vw, 110px); margin-top: clamp(12px, 3vh, 36px); }
.onair-guest { display: flex; flex-direction: column; align-items: center; width: clamp(140px, 24vw, 220px); }
.onair-avatar { position: relative; width: var(--onair-avatar, 150px); height: var(--onair-avatar, 150px); border-radius: 50%; overflow: hidden; background: ${GREY_SOFT}; box-shadow: 0 0 0 3px ${GREY}; transition: box-shadow .35s ease, transform .35s ease; }
.onair-avatar img { display: block; width: 100%; height: 100%; object-fit: cover; object-position: 50% 18%; }
.onair-avatar-fallback { display: flex; width: 100%; height: 100%; align-items: center; justify-content: center; font-family: ${zineFonts.cond}; font-weight: 500; font-size: clamp(40px, 8vw, 64px); letter-spacing: .04em; color: ${zineColors.warmGray}; }
.onair-avatar[data-host="true"] { box-shadow: 0 0 0 3px ${RED}; }
.onair-avatar[data-host="true"] .onair-avatar-fallback { background: ${RED}; color: #fff; }
.onair-avatar[data-quiet="true"] { box-shadow: 0 0 0 3px ${GREY}; }
.onair-avatar[data-speaking="true"] { box-shadow: 0 0 0 3px ${RED}, 0 0 0 12px rgba(212,25,32,.14); transform: scale(1.03); animation: onairGlow 1.4s ease-in-out infinite; }
.onair-name { margin: 12px 0 0; font-family: ${zineFonts.cond}; font-weight: 600; font-size: clamp(14px, 1.6vw, 20px); letter-spacing: .1em; text-transform: uppercase; color: ${INK}; white-space: nowrap; }
.onair-wave { display: flex; align-items: center; justify-content: center; gap: 5px; height: 26px; margin-top: 8px; }
.onair-wave span { display: block; width: 4px; height: 24px; border-radius: 2px; background: ${GREY}; transform: scaleY(${BAR_FLAT}); transform-origin: center; transition: background .3s ease; will-change: transform; }
.onair-wave[data-active="true"] span { background: ${RED}; }
.onair-status { height: 20px; margin-top: 4px; display: flex; align-items: center; justify-content: center; gap: 6px; font-size: 12px; color: ${zineColors.warmGray}; }
.onair-dots { display: inline-flex; gap: 4px; }
.onair-dots i { width: 5px; height: 5px; border-radius: 50%; background: ${RED}; animation: onairDot 1.2s ease-in-out infinite; }
.onair-dots i:nth-child(2) { animation-delay: .15s; }
.onair-dots i:nth-child(3) { animation-delay: .3s; }
.onair-caption { position: relative; display: flex; align-items: flex-start; justify-content: center; flex: 0 0 auto; height: calc(${CAPTION_LINES} * 1.25em); overflow: hidden; margin: clamp(10px, 2.5vh, 32px) auto 0; max-width: 820px; width: 100%; text-align: center; font-family: ${zineFonts.serif}; font-style: italic; font-size: clamp(22px, min(4vw, 5.2vh), 40px); line-height: 1.25; color: ${INK}; }
.onair-caption[data-small="true"] { align-items: center; }
.onair-caption-roll { width: 100%; transition: transform .45s cubic-bezier(.2,.7,.2,1); will-change: transform; }
.onair-caption-text { margin: 0; font: inherit; color: inherit; animation: onairCaptionIn .45s ease-out both; }
.onair-caption-text[data-live="true"] { animation: none; }
.onair-caption-text[data-dim="true"] { opacity: .4; }
.onair-caption[data-small="true"] .onair-caption-text { font-family: inherit; font-style: normal; font-size: clamp(16px, 2vw, 20px); color: ${zineColors.mid}; }
.onair-notice { margin: 14px auto 0; text-align: center; font-size: 13px; font-weight: 600; color: ${zineColors.warmGray}; }
.onair-notice[data-tone="alert"] { color: ${RED}; }
.onair-bottom { position: relative; display: flex; flex-direction: column; align-items: center; margin-top: clamp(10px, 2.5vh, 28px); padding-bottom: 4px; }
.onair-mic-wrap { position: relative; width: var(--onair-mic, 96px); height: var(--onair-mic, 96px); }
.onair-mic { position: relative; z-index: 1; display: flex; width: var(--onair-mic, 96px); height: var(--onair-mic, 96px); align-items: center; justify-content: center; border: 0; border-radius: 50%; background: ${RED}; color: #fff; cursor: pointer; touch-action: none; user-select: none; -webkit-user-select: none; -webkit-tap-highlight-color: transparent; box-shadow: 0 10px 24px rgba(212,25,32,.28); transition: transform .15s ease, background .2s ease, box-shadow .2s ease; }
.onair-mic:focus-visible { outline: 3px solid ${INK}; outline-offset: 4px; }
.onair-mic:disabled { cursor: default; opacity: .55; }
.onair-mic[data-state="pressed"] { transform: scale(.92); background: #B5131A; box-shadow: 0 4px 12px rgba(212,25,32,.35); }
.onair-mic[data-state="handsfree"] { background: ${INK}; box-shadow: 0 0 0 4px ${zineColors.paper}, 0 0 0 7px ${RED}; }
.onair-ring { position: absolute; inset: 0; border-radius: 50%; border: 2px solid rgba(212,25,32,.45); opacity: 0; pointer-events: none; }
.onair-mic-wrap[data-live="true"] .onair-ring { animation: onairSonar 1.6s ease-out infinite; }
.onair-mic-wrap[data-live="true"] .onair-ring:nth-child(2) { animation-delay: .55s; }
.onair-mic-wrap[data-live="true"] .onair-ring:nth-child(3) { animation-delay: 1.1s; }
.onair-mic-label { margin: 12px 0 0; font-family: ${zineFonts.cond}; font-weight: 600; font-size: 18px; letter-spacing: .12em; text-transform: uppercase; color: ${INK}; text-align: center; min-height: 24px; }
.onair-mic-label[data-live="true"] { color: ${RED}; }
.onair-mic-hint { margin: 6px 0 0; font-size: 13px; color: ${zineColors.warmGray}; text-align: center; min-height: 18px; }
.onair-end { position: absolute; right: 0; bottom: 4px; padding: 6px 2px; border: 0; background: none; font: inherit; font-size: 15px; color: ${zineColors.warmGray}; text-decoration: underline; text-underline-offset: 4px; cursor: pointer; }
.onair-end:hover:not(:disabled) { color: ${INK}; }
.onair-end:disabled { opacity: .4; cursor: default; }
@keyframes onairBlink { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: .35; transform: scale(.8); } }
@keyframes onairGlow { 0%, 100% { box-shadow: 0 0 0 3px ${RED}, 0 0 0 12px rgba(212,25,32,.14); } 50% { box-shadow: 0 0 0 3px ${RED}, 0 0 0 18px rgba(212,25,32,.05); } }
@keyframes onairDot { 0%, 80%, 100% { transform: translateY(0); opacity: .4; } 40% { transform: translateY(-4px); opacity: 1; } }
@keyframes onairSonar { 0% { transform: scale(1); opacity: .7; } 100% { transform: scale(2.1); opacity: 0; } }
@keyframes onairCaptionIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@media (max-height: 760px) {
  .onair { --onair-avatar: 116px; --onair-mic: 84px; }
  .onair-mic svg { width: 34px !important; height: 34px !important; }
  .onair-mic-label { font-size: 16px; }
  .onair-caption { height: calc(2 * 1.25em); }
}
@media (max-width: 560px) {
  .onair { min-height: 480px; padding: 12px 12px 16px; }
  .onair-timer { font-size: 17px; }
  .onair-guests { gap: 28px; }
  .onair-guest { width: 150px; }
  .onair { --onair-avatar: 112px; }
  .onair-name { font-size: 13px; }
  .onair-end { position: static; margin-top: 18px; align-self: flex-end; }
}
@media (prefers-reduced-motion: reduce) {
  .onair-pill-dot, .onair-rec[data-recording="true"]::before, .onair-avatar[data-speaking="true"], .onair-dots i, .onair-mic-wrap[data-live="true"] .onair-ring { animation: none; }
  .onair-mic-wrap[data-live="true"] .onair-ring { opacity: .5; transform: scale(1.25); }
  .onair-mic-wrap[data-live="true"] .onair-ring:nth-child(2), .onair-mic-wrap[data-live="true"] .onair-ring:nth-child(3) { display: none; }
  .onair-caption-text { animation: none; }
  .onair-avatar, .onair-mic { transition: none; }
}
`;

const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';
function queryReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(REDUCED_QUERY).matches
    : false;
}
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(queryReducedMotion);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(REDUCED_QUERY);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

function formatTime(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/** Paints one waveform from a 0..1 level: smooth attack/release plus a slow per-bar wobble so it never looks frozen. */
function paintBars(bars: HTMLSpanElement[], current: number[], level: number, active: boolean, now: number) {
  for (let i = 0; i < bars.length; i += 1) {
    const wobble = 0.8 + 0.2 * Math.sin(now / 110 + i * 1.3);
    const target = active ? BAR_FLAT + Math.min(1, level * 1.15) * BAR_SHAPE[i] * wobble * (1 - BAR_FLAT) : BAR_FLAT;
    const prev = current[i] ?? BAR_FLAT;
    // Rise fast, fall slower — reads as a voice, not a strobe.
    const next = target > prev ? prev + (target - prev) * 0.55 : prev + (target - prev) * 0.25;
    current[i] = next;
    bars[i].style.transform = `scaleY(${next.toFixed(3)})`;
  }
}

function Waveform({ active, label, waveRef }: { active: boolean; label: string; waveRef: React.RefObject<HTMLDivElement | null> }) {
  return (
    <div ref={waveRef} className="onair-wave" data-active={active ? 'true' : 'false'} role="img" aria-label={label}>
      {Array.from({ length: BAR_COUNT }, (_, i) => (
        <span key={i} />
      ))}
    </div>
  );
}

export function OnAirCallView({
  hostLine,
  aiLiveTranscript = '',
  isRecording,
  isAiSpeaking,
  isAiThinking,
  currentTranscript,
  timeRemaining,
  idleSecondsLeft,
  quietSeconds,
  isWrappingUp,
  conversationState,
  error,
  getLevels,
  hostImageSrc,
  guestInitials,
  guestName,
  topicTitle,
  settingsSlot,
  onEndConversation,
  onToggleRecording,
  onPointerDown,
  onPointerUp,
}: OnAirCallViewProps) {
  const { t } = useTranslation('p2e');
  const reducedMotion = usePrefersReducedMotion();
  const [pressed, setPressed] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  // Size the stage to the screen: from its top edge to the bottom of the viewport
  // (minus the page's bottom gap), so the mic never falls below the fold. The CSS
  // floor (min-height) still applies on very short screens, which then scroll.
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof window === 'undefined') return;
    const fit = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      const room = window.innerHeight - top - 16;
      el.style.setProperty('--onair-h', `${Math.max(440, Math.round(room))}px`);
    };
    fit();
    window.addEventListener('resize', fit);
    const id = window.setTimeout(fit, 150);
    return () => {
      window.removeEventListener('resize', fit);
      window.clearTimeout(id);
    };
  }, []);
  const hostWaveRef = useRef<HTMLDivElement>(null);
  const guestWaveRef = useRef<HTMLDivElement>(null);
  const hostLevelsRef = useRef<number[]>([]);
  const guestLevelsRef = useRef<number[]>([]);

  const isEnding = conversationState === 'ending';
  const hostName = t('mellinai.call.hostName');
  const youName = guestName?.trim() || t('mellinai.call.you');
  const initials = (guestInitials?.trim() || t('mellinai.call.initialsFallback')).slice(0, 2).toUpperCase();
  // Nudge only when the line is truly quiet and no stronger notice (countdown / wrap-up) is on screen.
  const showQuietHint =
    quietSeconds >= QUIET_HINT_SEC && idleSecondsLeft === null && !isWrappingUp && !isAiSpeaking && !isAiThinking;
  const thinking = isAiThinking && !isAiSpeaking && !isRecording;
  const lastMinute = timeRemaining <= 60;

  // Drive both waveforms from the real audio levels, outside React (one rAF loop).
  useEffect(() => {
    const bars = (wave: HTMLDivElement | null) => Array.from(wave?.querySelectorAll('span') ?? []);
    const hostBars = bars(hostWaveRef.current);
    const guestBars = bars(guestWaveRef.current);
    if (reducedMotion) {
      // Static state changes only: active = medium bars, quiet = flat.
      hostBars.forEach((bar, i) => {
        bar.style.transform = `scaleY(${isAiSpeaking ? 0.3 + BAR_SHAPE[i] * 0.5 : BAR_FLAT})`;
      });
      guestBars.forEach((bar, i) => {
        bar.style.transform = `scaleY(${isRecording ? 0.3 + BAR_SHAPE[i] * 0.5 : BAR_FLAT})`;
      });
      return;
    }
    let frame = 0;
    const tick = () => {
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const levels = getLevels();
      paintBars(hostBars, hostLevelsRef.current, levels.ai, isAiSpeaking, now);
      paintBars(guestBars, guestLevelsRef.current, levels.mic, isRecording, now);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [getLevels, isAiSpeaking, isRecording, reducedMotion]);

  // Keep the pointer on the button while holding, so a finger drifting off the
  // edge does not cut the recording in half.
  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (isEnding) return;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // ignore: capture is best-effort
    }
    setPressed(true);
    onPointerDown();
  };
  const handlePointerUp = () => {
    setPressed(false);
    onPointerUp();
  };

  const micState = pressed ? 'pressed' : isRecording ? 'handsfree' : 'idle';
  const micLive = pressed || isRecording;
  const micLabel = pressed
    ? t('mellinai.call.listening')
    : isRecording
      ? t('mellinai.call.handsFreeActive')
      : t('mellinai.call.holdToTalk');

  // Her words, paced to her voice. The transcript streams in ahead of the audio, so
  // while she speaks we reveal ~2.6 words/s from the moment her voice started; once
  // the voice stops (or motion is reduced) the whole sentence shows.
  const liveWords = useMemo(() => aiLiveTranscript.split(/\s+/).filter(Boolean), [aiLiveTranscript]);
  const [revealed, setRevealed] = useState(0);
  const speechStartRef = useRef<number | null>(null);
  useEffect(() => {
    if (liveWords.length === 0) setRevealed(0);
  }, [liveWords.length]);
  useEffect(() => {
    if (!isAiSpeaking || reducedMotion) {
      speechStartRef.current = null;
      setRevealed(liveWords.length);
      return;
    }
    if (speechStartRef.current === null) speechStartRef.current = Date.now();
    const tick = () => {
      const start = speechStartRef.current ?? Date.now();
      const byTime = 1 + Math.floor(((Date.now() - start) / 1000) * CAPTION_WORDS_PER_SEC);
      setRevealed((prev) => Math.max(prev, Math.min(liveWords.length, byTime)));
    };
    tick();
    const id = window.setInterval(tick, 120);
    return () => window.clearInterval(id);
  }, [isAiSpeaking, liveWords.length, reducedMotion]);
  const liveText = liveWords.slice(0, revealed).join(' ');
  const hostText = liveText || hostLine;
  const hostKey = liveText ? 'live' : hostLine;
  const isLive = isAiSpeaking && revealed < liveWords.length;

  // What the big caption shows, in priority order.
  let caption: { text: string; key: string; dim?: boolean; small?: boolean; status?: boolean; live?: boolean } | null = null;
  if (isRecording) {
    if (showQuietHint) caption = { text: t('zine.speak.goAhead'), key: 'quiet', small: true, status: true };
    else if (currentTranscript) caption = { text: currentTranscript, key: 'you', small: true };
    else if (hostText) caption = { text: hostText, key: hostKey, dim: true };
  } else if (showQuietHint) {
    caption = { text: t('zine.speak.goAhead'), key: 'quiet', small: true, status: true };
  } else if (hostText) {
    caption = { text: hostText, key: hostKey, dim: isAiThinking && !liveText, live: isLive };
  }

  // Long sentences roll upwards inside the fixed window so the latest words stay in view.
  const captionRef = useRef<HTMLDivElement>(null);
  const rollRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const box = captionRef.current;
    const roll = rollRef.current;
    if (!box || !roll) return;
    const overflow = Math.max(0, roll.scrollHeight - box.clientHeight);
    roll.style.transform = overflow > 0 ? `translateY(-${overflow}px)` : 'none';
  }, [caption?.text, caption?.small]);

  return (
    <div className="onair" data-testid="onair-call" ref={stageRef}>
      <style>{STYLES}</style>

      <div className="onair-top">
        <span className="onair-pill" aria-label={t('mellinai.call.onAir')}>
          {t('mellinai.call.onAir')}
          <span className="onair-pill-dot" aria-hidden="true" />
        </span>
        <div className="onair-meta">
          <span
            className="onair-timer"
            data-last-minute={lastMinute ? 'true' : 'false'}
            aria-label={`${t('mellinai.call.timerLabel')} ${formatTime(timeRemaining)}`}
            data-testid="onair-timer"
          >
            {formatTime(timeRemaining)}
          </span>
          <span className="onair-rec" data-recording={isRecording ? 'true' : 'false'}>
            {t('mellinai.call.rec')}
          </span>
          {settingsSlot}
        </div>
      </div>

      {topicTitle && <p className="onair-eyebrow">{topicTitle}</p>}
      {error && (
        <p className="onair-error" role="alert">
          {error}
        </p>
      )}

      <div className="onair-guests">
        <div className="onair-guest" data-testid="onair-host">
          <div
            className="onair-avatar"
            data-host="true"
            data-speaking={isAiSpeaking ? 'true' : 'false'}
            data-quiet={!isAiSpeaking && isRecording ? 'true' : 'false'}
          >
            {hostImageSrc ? (
              <img src={hostImageSrc} alt={hostName} />
            ) : (
              <span className="onair-avatar-fallback" aria-hidden="true">
                M
              </span>
            )}
          </div>
          <p className="onair-name">
            {hostName} · {t('mellinai.call.host')}
          </p>
          <Waveform
            active={isAiSpeaking}
            waveRef={hostWaveRef}
            label={t(isAiSpeaking ? 'mellinai.call.speaking' : 'mellinai.call.quiet', { name: hostName })}
          />
          <div className="onair-status" role="status" aria-live="polite">
            {thinking && (
              <>
                <span className="onair-dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
                <span>{t('mellinai.call.thinking')}</span>
              </>
            )}
          </div>
        </div>

        <div className="onair-guest" data-testid="onair-guest">
          <div className="onair-avatar" data-speaking={isRecording ? 'true' : 'false'} aria-hidden="true">
            <span className="onair-avatar-fallback">{initials}</span>
          </div>
          <p className="onair-name">
            {t('mellinai.call.you')} · {t('mellinai.call.guest')}
          </p>
          <Waveform
            active={isRecording}
            waveRef={guestWaveRef}
            label={t(isRecording ? 'mellinai.call.speaking' : 'mellinai.call.quiet', { name: youName })}
          />
          <div className="onair-status" />
        </div>
      </div>

      <div
        className="onair-caption"
        ref={captionRef}
        aria-live={isAiSpeaking ? 'off' : 'polite'}
        aria-atomic="true"
        data-small={caption?.small ? 'true' : 'false'}
        data-testid="onair-caption"
      >
        {caption && (
          <div className="onair-caption-roll" ref={rollRef}>
            <p
              key={caption.key}
              className="onair-caption-text"
              data-dim={caption.dim ? 'true' : 'false'}
              data-live={caption.live ? 'true' : 'false'}
              role={caption.status ? 'status' : undefined}
            >
              {caption.text}
            </p>
          </div>
        )}
      </div>

      {idleSecondsLeft !== null && !isRecording && (
        <p className="onair-notice" data-tone="alert" role="status">
          {t('zine.speak.idleCountdown', { seconds: idleSecondsLeft })}
        </p>
      )}
      {idleSecondsLeft === null && isWrappingUp && (
        <p className="onair-notice" role="status">
          {t('zine.speak.wrappingUp')}
        </p>
      )}

      <div className="onair-bottom">
        <div className="onair-mic-wrap" data-live={micLive ? 'true' : 'false'}>
          <span className="onair-ring" aria-hidden="true" />
          <span className="onair-ring" aria-hidden="true" />
          <span className="onair-ring" aria-hidden="true" />
          <button
            type="button"
            className="onair-mic"
            data-state={micState}
            aria-pressed={isRecording}
            aria-label={t('mellinai.call.micLabel')}
            disabled={isEnding}
            onClick={onToggleRecording}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onLostPointerCapture={handlePointerUp}
          >
            <Mic style={{ width: 40, height: 40 }} strokeWidth={2.2} aria-hidden="true" />
          </button>
        </div>
        <p className="onair-mic-label" data-live={micLive ? 'true' : 'false'} data-testid="onair-mic-label">
          {micLabel}
        </p>
        <p className="onair-mic-hint">{!micLive ? t('mellinai.call.handsFreeHint') : ''}</p>
        <button
          type="button"
          className="onair-end"
          disabled={isEnding || isRecording}
          onClick={onEndConversation}
          data-testid="onair-end"
        >
          {isEnding ? t('mellinai.call.ending') : t('mellinai.call.end')}
        </button>
      </div>
    </div>
  );
}
